import { useMemo } from "react";
import { Bar, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Btn, CTooltip, Card, SumCard, useIsMobile } from "components/ui/index";
import { MONTHS_SHORT } from "constants/index";
import { T } from "theme/index";
import { getMoMComparison, getMonthlyBreakdown, getExpenseByCategory, filterByMonth } from "engine/index";
import { getWalletMonth } from "engine/wallets";
import { fmtIDR, fmtShort } from "utils/format";

// ── Dashboard ─────────────────────────────────────────────────────────────────
export default function Dashboard({ transactions, categories, investments, wallets, month, year, onAddTx }) {
  const isMobile = useIsMobile();
  // ── Engine calls ──
  const { income: totalIncome, expense: totalExpense, investasi: totalInvOut, net,
          prevIncome, prevExpense, prevInvestasi: prevInvOut, prevNet } =
    useMemo(() => getMoMComparison(transactions, month, year), [transactions, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  // 12 bulan penuh untuk tahun yang dipilih
  const trend = useMemo(() =>
    getMonthlyBreakdown(transactions, year, MONTHS_SHORT),
  [transactions, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const byCat = useMemo(() =>
    getExpenseByCategory(transactions, categories, month, year, "month")
      .map(c => ({ name: c.name, value: c.total, color: c.color, icon: c.icon })),
  [transactions, categories, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const recent = useMemo(() =>
    [...filterByMonth(transactions, month, year)].sort((a,b) => new Date(b.date)-new Date(a.date)).slice(0,7),
  [transactions, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:16 }}>
        <SumCard label="Pemasukan"    value={totalIncome}  color={T.green} prefix="+" prev={prevIncome}  upGood={true} />
        <SumCard label="Pengeluaran"  value={totalExpense} color={T.red}               prev={prevExpense} upGood={false} />
        <SumCard label="Saldo Bersih" value={net} color={net>=0?T.green:T.red} prefix={net>=0?"+":""} prev={prevNet} upGood={true} />
        <SumCard label="Investasi"    value={totalInvOut}  color={T.gold}              prev={prevInvOut}  upGood={true} />
      </div>

      {/* Wallet strip — sisa per rekening bulan ini */}
      {wallets.length > 0 && (
        <>
          <div style={{ color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Sisa Per Rekening — {MONTHS_SHORT[month]} {year}</div>
          <div style={{ display:"flex", gap:10, overflowX:"auto", marginBottom:16, paddingBottom:4 }}>
            {wallets.map(w => {
              const { balance } = getWalletMonth(w, transactions, month, year);
              return (
                <div key={w.id} style={{ flexShrink:0, background:w.color+"15", border:`1px solid ${w.color}30`, borderRadius:10, padding:"10px 14px", minWidth:140 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                    <span style={{ fontSize:16 }}>{w.icon}</span>
                    <span style={{ color:T.muted, fontSize:11, fontWeight:600 }}>{w.name}</span>
                  </div>
                  <div style={{ color:balance>=0?w.color:T.red, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700, fontSize:14 }}>{fmtShort(balance)}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Card style={{ padding:16, marginBottom:14 }}>
        <div style={{ color:T.text, fontWeight:700, fontSize:13, marginBottom:14 }}>📊 Tren 12 Bulan — {year}</div>
        <ResponsiveContainer width="100%" height={isMobile?180:220}>
          <ComposedChart data={trend} margin={{ top:4, right:4, left:0, bottom:0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fill:T.muted, fontSize:10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill:T.muted, fontSize:9 }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={52} />
            <Tooltip content={<CTooltip />} />
            <Bar     dataKey="income"    name="income"    fill={T.gold}  radius={[3,3,0,0]} maxBarSize={22} opacity={0.85} />
            <Line    dataKey="investasi" name="investasi" stroke={T.blue} strokeWidth={2} dot={{ r:3, fill:T.blue, stroke:"#00000066", strokeWidth:1 }} activeDot={{ r:5 }} type="linear" />
            <Line    dataKey="expense"   name="expense"   stroke={T.red}   strokeWidth={2} dot={{ r:3, fill:T.red,   stroke:"#00000066", strokeWidth:1 }} activeDot={{ r:5 }} type="linear" />
          </ComposedChart>
        </ResponsiveContainer>
        <div style={{ display:"flex", gap:14, justifyContent:"center", marginTop:8, flexWrap:"wrap" }}>
          {[[T.gold,"Pemasukan","rect"],[T.blue,"Investasi","line"],[T.red,"Pengeluaran","line"]].map(([c,l,shape]) => (
            <div key={l} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:T.muted }}>
              {shape==="line"
                ? <div style={{ width:16, height:2, background:c, borderRadius:1, position:"relative" }}>
                    <div style={{ width:5, height:5, borderRadius:"50%", background:c, position:"absolute", top:-1.5, left:5 }} />
                  </div>
                : <div style={{ width:10, height:10, borderRadius:2, background:c, opacity:0.85 }} />
              }
              {l}
            </div>
          ))}
        </div>
      </Card>

      {byCat.length > 0 && (
        <Card style={{ padding:16, marginBottom:14 }}>
          <div style={{ color:T.text, fontWeight:700, fontSize:13, marginBottom:12 }}>🥧 Pengeluaran Bulan Ini</div>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <ResponsiveContainer width={isMobile?100:130} height={isMobile?100:130}>
              <PieChart>
                <Pie data={byCat} cx="50%" cy="50%" innerRadius={30} outerRadius={48} dataKey="value" paddingAngle={2}>
                  {byCat.map((e,i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={v=>fmtIDR(v)} contentStyle={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, fontSize:11, color:T.text }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
              {byCat.slice(0,5).map((c,i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11 }}>
                    <div style={{ width:6, height:6, borderRadius:2, background:c.color, flexShrink:0 }} />
                    <span style={{ color:T.muted }}>{c.icon} {c.name}</span>
                  </div>
                  <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontSize:11 }}>{isMobile ? fmtShort(c.value) : fmtIDR(c.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px 10px" }}>
          <div style={{ color:T.text, fontWeight:700, fontSize:13 }}>🕐 Transaksi Terbaru</div>
          <Btn onClick={onAddTx} style={{ padding:"7px 12px", fontSize:12 }}>+ Tambah</Btn>
        </div>
        {recent.length === 0 ? (
          <div style={{ textAlign:"center", padding:"36px 0", color:T.muted, fontSize:13 }}>
            Belum ada transaksi bulan ini
          </div>
        ) : recent.map((tx, idx) => {
          const cat = categories.find(c=>c.id===tx.categoryId);
          return (
            <div key={tx.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", borderBottom:idx<recent.length-1?`1px solid ${T.border}`:"none" }}>
              <div style={{ width:36, height:36, borderRadius:9, background:(cat?.color||T.muted)+"1E", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{cat?.icon||"📌"}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:T.text, fontSize:13, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tx.description||cat?.name||"Transaksi"}</div>
                <div style={{ color:T.muted, fontSize:11, marginTop:1 }}>{cat?.name} · {new Date(tx.date).toLocaleDateString("id-ID",{day:"numeric",month:"short"})}</div>
              </div>
              <div style={{ color:tx.type==="income"?T.green:T.red, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700, fontSize:13, whiteSpace:"nowrap" }}>
                {tx.type==="income"?"+":"-"}{fmtShort(tx.amount)}
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
